import { useEffect, useState } from 'react';
import { adminApi } from '../../services/portalApi';
import type { PortalUser } from '../../types';

type DisplayRole = 'admin' | 'control room' | 'view only';

const ROLE_LABELS: Record<string, DisplayRole> = {
  admin:      'admin',
  guardhouse: 'control room',
  trustee:    'view only',
};

const ROLE_FROM_DISPLAY: Record<DisplayRole, string> = {
  'admin':      'admin',
  'control room': 'guardhouse',
  'view only':  'trustee',
};

const ROLE_BADGE: Record<string, string> = {
  admin:      'bg-brand-100 text-brand-700',
  guardhouse: 'bg-purple-100 text-purple-700',
  trustee:    'bg-gray-100 text-gray-600',
};

interface AddForm { email: string; display_name: string; role: DisplayRole; password: string; }
interface EditForm { display_name: string; role: DisplayRole; password: string; }

const EMPTY_ADD: AddForm = { email: '', display_name: '', role: 'control room', password: '' };

export default function PortalUsers() {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd]   = useState(false);
  const [addForm, setAddForm]   = useState<AddForm>(EMPTY_ADD);
  const [addError, setAddError] = useState('');
  const [saving, setSaving]     = useState(false);

  const [editUser, setEditUser]   = useState<PortalUser | null>(null);
  const [editForm, setEditForm]   = useState<EditForm>({ display_name: '', role: 'control room', password: '' });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(null);

  function refresh() {
    adminApi.listPortalUsers()
      .then((all) => setUsers(all.filter((u) => !u.email.startsWith('patrolitadmin@'))));
  }

  useEffect(() => {
    adminApi.listPortalUsers()
      .then((all) => setUsers(all.filter((u) => !u.email.startsWith('patrolitadmin@'))))
      .finally(() => setLoading(false));
  }, []);

  function setAddField<K extends keyof AddForm>(k: K, v: AddForm[K]) {
    setAddForm((prev) => ({ ...prev, [k]: v }));
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAddError('');
    if (addForm.password.length < 8) { setAddError('Password must be at least 8 characters.'); return; }
    setSaving(true);
    try {
      await adminApi.createPortalUser({
        email:        addForm.email,
        display_name: addForm.display_name,
        role:         ROLE_FROM_DISPLAY[addForm.role],
        password:     addForm.password,
      });
      setShowAdd(false);
      setAddForm(EMPTY_ADD);
      refresh();
    } catch {
      setAddError('Failed to create user. Email may already be in use.');
    } finally {
      setSaving(false);
    }
  }

  function openEdit(u: PortalUser) {
    setEditUser(u);
    setEditForm({
      display_name: u.display_name,
      role:         ROLE_LABELS[u.role] ?? 'control room',
      password:     '',
    });
    setEditError('');
    setShowEditPassword(false);
  }

  async function handleEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editUser) return;
    setEditError('');
    if (editForm.password && editForm.password.length < 8) {
      setEditError('New password must be at least 8 characters.');
      return;
    }
    setEditSaving(true);
    try {
      const data: Parameters<typeof adminApi.updatePortalUser>[1] = {
        display_name: editForm.display_name,
        role:         ROLE_FROM_DISPLAY[editForm.role],
      };
      if (editForm.password) data.password = editForm.password;
      await adminApi.updatePortalUser(editUser.id, data);
      setEditUser(null);
      refresh();
    } catch {
      setEditError('Failed to update user.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDeactivate(id: string) {
    await adminApi.updatePortalUser(id, { is_active: false });
    setUsers((prev) => prev.map((u) => u.id === id ? { ...u, is_active: false } : u));
    setConfirmDeactivate(null);
  }

  async function handleDelete(id: string) {
    setDeleteError('');
    try {
      await adminApi.deletePortalUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setConfirmDelete(null);
    } catch {
      setDeleteError('Could not delete user. Please try again.');
      setConfirmDelete(null);
    }
  }

  if (loading) return <div className="px-6 py-12 text-gray-400">Loading…</div>;

  return (
    <div className="px-6 py-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Portal Users</h1>
          <p className="text-sm text-gray-500">{users.length} user{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => { setShowAdd((v) => !v); setAddError(''); }}
          className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          + Add user
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="bg-blue-50 border border-brand-200 rounded-xl p-5 mb-6 space-y-3">
          <h2 className="font-medium text-gray-800 text-sm">New portal user</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Full name</label>
              <input type="text" required value={addForm.display_name}
                onChange={(e) => setAddField('display_name', e.target.value)}
                placeholder="Jane Smith"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input type="email" required value={addForm.email}
                onChange={(e) => setAddField('email', e.target.value)}
                placeholder="jane@estate.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
              <input type="password" required value={addForm.password}
                onChange={(e) => setAddField('password', e.target.value)}
                placeholder="Min 8 characters"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select value={addForm.role} onChange={(e) => setAddField('role', e.target.value as DisplayRole)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white">
                <option value="admin">Admin</option>
                <option value="control room">Control Room</option>
                <option value="view only">View only</option>
              </select>
            </div>
          </div>
          {addError && <p className="text-sm text-red-600">{addError}</p>}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60">
              {saving ? 'Saving…' : 'Create user'}
            </button>
            <button type="button" onClick={() => { setShowAdd(false); setAddForm(EMPTY_ADD); setAddError(''); }}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </form>
      )}

      {deleteError && (
        <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{deleteError}</p>
      )}

      {/* User table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {users.length === 0 ? (
          <div className="py-12 text-center text-gray-400">No portal users yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 font-semibold text-gray-600">Name</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Email</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Role</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{u.display_name}</td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ROLE_BADGE[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                      {ROLE_LABELS[u.role] ?? u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => openEdit(u)}
                        className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 hover:bg-blue-200">
                        Edit
                      </button>
                      {u.is_active && (
                        confirmDeactivate === u.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDeactivate(u.id)}
                              className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">Confirm</button>
                            <button onClick={() => setConfirmDeactivate(null)}
                              className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 hover:bg-gray-200">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => setConfirmDeactivate(u.id)}
                            className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200">
                            Deactivate
                          </button>
                        )
                      )}
                      {confirmDelete === u.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleDelete(u.id)}
                            className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">Confirm delete</button>
                          <button onClick={() => setConfirmDelete(null)}
                            className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 hover:bg-gray-200">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDelete(u.id)}
                          className="px-2 py-1 text-xs rounded bg-red-100 text-red-800 hover:bg-red-200">
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Edit user — {editUser.email}</h2>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full name</label>
                <input type="text" required value={editForm.display_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <select value={editForm.role}
                  onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value as DisplayRole }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white">
                  <option value="admin">Admin</option>
                  <option value="control room">Control Room</option>
                  <option value="view only">View only</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">New password <span className="text-gray-400">(leave blank to keep current)</span></label>
                <div className="relative">
                  <input type={showEditPassword ? 'text' : 'password'} value={editForm.password}
                    onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Min 8 characters"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  <button type="button" onClick={() => setShowEditPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">
                    {showEditPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              {editError && <p className="text-sm text-red-600">{editError}</p>}
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={editSaving}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-60">
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
                <button type="button" onClick={() => setEditUser(null)}
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
